<template>
  <div v-if="visible" class="p-dialog-wrapper" @click="closeableOutside ? closeOnOutsideClick : null">
    <div class="p-dialog" :style="dialogStyle" :class="{ 'content-only': hideHeader, 'question-dialog': isQuestion }"
      @click.stop>
      <div class="p-dialog-header" v-if="!hideHeader">
        <h5 class="p-dialog-title">{{ header || title }}</h5>
        <button v-if="closable" class="p-dialog-close" @click="close">×</button>
      </div>
      <div class="p-dialog-content"
        :class="{ 'full-height': hideHeader, 'transparent-content': transparentBackground }">
        <slot></slot>
      </div>
      <div class="p-dialog-footer" v-if="$slots.footer">
        <slot name="footer"></slot>
      </div>
    </div>
  </div>
</template>

<script>
/**
 * @component CustomDialog
 * @description Componente de diálogo modal personalizado.
 * Permite mostrar información o formularios en una ventana modal con fondo oscurecido.
 */
export default {
  name: 'CustomDialog',
  props: {
    /**
     * Determina si el diálogo es visible
     */
    visible: {
      type: Boolean,
      default: false
    },
    /**
     * Título que se muestra en el encabezado del diálogo
     */
    header: {
      type: String,
      default: ''
    },
    /**
     * Alias para header (compatibilidad)
     */
    title: {
      type: String,
      default: ''
    },
    breakpoints: {
      type: Object,
      default: () => ({})
    },
    style: {
      type: Object,
      default: () => ({})
    },
    maximizable: {
      type: Boolean,
      default: false
    },
    modal: {
      type: Boolean,
      default: true
    },
    /**
     * Oculta el encabezado del diálogo
     */
    hideHeader: {
      type: Boolean,
      default: false
    },
    /**
     * Hace que el fondo del diálogo sea transparente
     */
    transparentBackground: {
      type: Boolean,
      default: false
    },
    /**
     * Determina si el diálogo debe cerrarse al hacer clic fuera de él
     */
    closeableOutside: {
      type: Boolean,
      default: true
    },
    /**
     * Determina si se muestra el botón de cerrar (X) en el header
     */
    closable: {
      type: Boolean,
      default: true
    },
    /**
     * Determina si es un diálogo de pregunta/confirmación (altura reducida)
     */
    isQuestion: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    dialogStyle() {
      const mergedStyle = {
        ...this.style
      };

      if (!mergedStyle.width) {
        mergedStyle.width = 'min(42rem, calc(100vw - 2rem))';
      }

      if (!mergedStyle.maxWidth) {
        mergedStyle.maxWidth = 'calc(100vw - 2rem)';
      }

      if (!mergedStyle.maxHeight) {
        mergedStyle.maxHeight = 'calc(100dvh - 2rem)';
      }

      if (!mergedStyle.minWidth) {
        mergedStyle.minWidth = 'min(24rem, calc(100vw - 2rem))';
      }

      return {
        ...mergedStyle
      };
    }
  },
  methods: {
    /**
     * Cierra el diálogo emitiendo el evento 'update:visible'
     */
    close() {
      this.$emit('update:visible', false);
      this.$emit('close');
    },
    /**
     * Cierra el diálogo al hacer clic fuera de él
     */
    closeOnOutsideClick() {
      this.close();
    }
  },
  emits: ['update:visible', 'close']
};
</script>

<style scoped>
.p-dialog-wrapper {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  box-sizing: border-box;
  background-color: rgba(0, 0, 0, 0.4);
  z-index: 1000;
  overflow-y: auto;
  backdrop-filter: blur(3px);
}

.p-dialog-wrapper.transparent-bg {
  background-color: transparent;
}

.p-dialog {
  display: flex;
  background-color: var(--modal-background);
  border-radius: 1rem; /* More rounded as per premium guide */
  border: 1px solid var(--modal-border);
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2); /* Deeper shadow for premium feel */
  width: min(42rem, calc(100vw - 2rem));
  min-width: min(24rem, calc(100vw - 2rem));
  max-width: calc(100vw - 2rem);
  max-height: calc(100dvh - 2rem);
  flex-direction: column;
  margin: auto;
  transition: all 0.3s ease;
}

/* En modo oscuro, usar borde premium */
body.dark .p-dialog,
body.dark-theme .p-dialog {
  border: 1px solid color-mix(in srgb, var(--primary-color, #74a80b) 40%, var(--modal-border));
}

.p-dialog.content-only {
  height: auto;
  min-height: min(32rem, calc(100dvh - 2rem));
}

.p-dialog.question-dialog {
  height: auto;
  min-height: 200px;
  width: min(32rem, calc(100vw - 2rem));
  max-width: calc(100vw - 2rem);
  margin: auto;
  border-radius: 0.75rem;
}

.p-dialog-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
 
  color: var(--text-color);
  flex-shrink: 0;
}

.p-dialog-title {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 600;
  color: var(--text-color);
}

.p-dialog-close {
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: var(--text-color);
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  border-radius: 50%;
  transition: background-color var(--transition-fast);
  padding: 0;
}

.p-dialog-close:hover {
  background-color: var(--surface-200);
}

.p-dialog-content {
  padding: 1rem;
  color: var(--text-color);
  flex: 1;
  
  overflow-y: auto;
  /* Para permitir scroll si el contenido es muy grande */
}

/* Toolbar utilitaria dentro del contenido del diálogo */
.dialog-toolbar {
  display: flex;
  gap: 10px;
  align-items: center;
  justify-content: flex-end;
  margin: 0.5rem 0 1rem 0;
}

.dialog-toolbar :deep(.p-button:hover:not(:disabled):not(.p-button-loading)) {
  background-color: var(--button-background-color);
  border-color: var(--input-border-color);
  box-shadow: none;
}

.p-dialog-footer {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  padding: 1rem;
  border-top: 1px solid var(--modal-border);
  background-color: var(--modal-footer-bg);
  gap: 0.5rem;
  flex-shrink: 0;
}

.p-dialog-content.full-height {
  height: 100%;
}

.transparent-bg {
  background-color: transparent;
  backdrop-filter: none;
}

.content-only {
  background-color: transparent;
  box-shadow: none;
}

.full-height {
  height: 100%;
}

.p-dialog-content.transparent-content {
  background-color: transparent;
}

/* Estilos específicos para diferentes resoluciones y zoom */


/* Estilos para zoom específico */
@media screen and (zoom: 110%) {
  .p-dialog {
    max-height: 85vh;
    margin: 15px;
  }
}

@media screen and (zoom: 125%) {
  .p-dialog {
    max-height: 80vh;
    margin: 10px;
  }
}

@media screen and (zoom: 150%) {
  .p-dialog {
    max-height: 75vh;
    margin: 5px;
  }
}

@media (max-width: 768px) {
  .p-dialog-wrapper {
    padding: 0.75rem;
    align-items: center;
  }

  .p-dialog {
    width: calc(100vw - 1.5rem);
    min-width: 0;
    max-width: calc(100vw - 1.5rem);
    height: auto;
    max-height: calc(100dvh - 1.5rem);
    margin: 0;
  }

  .p-dialog-header {
    padding: 0.75rem;
  }

  .p-dialog-content {
    padding: 0.75rem;
  }
}

@media (max-height: 820px) {
  .p-dialog-wrapper {
    align-items: center;
  }

  .p-dialog {
    max-height: calc(100dvh - 1.5rem);
  }
}
</style>
